import { Injectable } from '@angular/core';
import { Answersheet } from '../../models/Answersheet';
import { VisualizedDataType } from '../../models/Diary';
import { DataCategory } from '../../components/data-category-chips/data-category-chips.component';

/**
 * represents a data point that can be visualized
 */
export interface DataPoint {
  timestamp: number;
  value: number;
  categoryId: string;
  source: 'diary' | 'sensor';
}

export type QuestionMetaMap = Map<number, { questionType: string; label: string }>;

/**
 * service for extracting and preparing data from diary answersheets for visualization
 */
@Injectable({
  providedIn: 'root'
})
export class DataVisualizationService {

  private readonly SENSOR_CATEGORIES: { [key: string]: { labelKey: string, path: (d: any) => any } } = {
      steps: { labelKey: 'DATA_VISUALIZATION.SENSOR_STEPS', path: (d: any) => d.pedometer?.steps }
    };

  constructor() { }

  /**
   * extract all available numerical data categories from answersheets
   */
  extractAvailableCategories(answersheets: Answersheet[], visualizedDataTypes: VisualizedDataType[] | undefined, elementMeta: QuestionMetaMap): DataCategory[] {
    if (!visualizedDataTypes?.length) return [];

    const categories = new Map<string, DataCategory>();
    const allowedTypes = new Set<string>(visualizedDataTypes);

    answersheets.forEach(sheet => {
      // extract sensor categories
      if (sheet.sensor_data?.pedometer?.status === 'success' &&
          sheet.sensor_data.pedometer.steps != null &&
          allowedTypes.has('SensorData_steps') &&
          !categories.has('steps')) {
        categories.set('steps', { id: 'steps', label: 'DATA_VISUALIZATION.SENSOR_STEPS' });
      }

      // extract diary answer categories - filter by question type if available
      this.getAnswersArray(sheet).forEach(answer => {
        const metaKey = answer.position;
        const meta = metaKey == null ? undefined : elementMeta.get(metaKey);
        const questionType = meta?.questionType;
        const label = meta?.label;

        if (typeof answer === 'object' && answer?.value !== null && questionType && this.isNumerical(answer.value, questionType)) {
          // answer must have a questionType in the allowed list
          if (!allowedTypes.has(questionType)) {
            return;
          }
          
          const id = this.getCategoryId(answer);
          if (id && !categories.has(id)) {
            let valueType: DataCategory['valueType'] = 'number';
            if (questionType === 'TextDate_date' || questionType === 'TextDate_datetime') {
              valueType = 'timestamp';
            } else if (questionType === 'TextDate_time') {
              valueType = 'minutes';
            }
            categories.set(id, { id, label: label || id, valueType, questionType });
          }
        }
      });
    });

    return Array.from(categories.values());
  }

  /**
   * extract data points for visualization from answersheets
   */
  extractDataPoints(
    answersheets: Answersheet[],
    categoryIds: string[],
    startDate: number | undefined,
    endDate: number | undefined,
    elementMeta: QuestionMetaMap
  ): DataPoint[] {
    return answersheets
      .filter(sheet => (!startDate || sheet.collected_at >= startDate) && (!endDate || sheet.collected_at <= endDate))
      .reduce((allPoints: DataPoint[], sheet) => {
        const points: DataPoint[] = [];

        // try sensor data
        categoryIds.forEach(categoryId => {
          if (this.SENSOR_CATEGORIES[categoryId] && sheet.sensor_data) {
            const value = this.SENSOR_CATEGORIES[categoryId].path(sheet.sensor_data);
            if (value != null) {
              points.push({ timestamp: sheet.collected_at, value, categoryId, source: 'sensor' });
            }
          }
        });

        // try answer data
        this.getAnswersArray(sheet).forEach(answer => {
          const categoryId = this.getCategoryId(answer);
          if (categoryId && categoryIds.includes(categoryId)) {
            const metaKey = answer.position;
            const questionType = metaKey == null ? undefined : elementMeta.get(metaKey)?.questionType;
            if (!questionType) return;
            const value = this.getNumericalValue(answer.value, questionType);
            if (value !== null) {
              points.push({ timestamp: sheet.collected_at, value, categoryId, source: 'diary' });
            }
          }
        });

        allPoints.push(...points);
        return allPoints;
      }, [])
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  private getNumericalValue(value: any, questionType: string): number | null {
    if (value === null || value === undefined) return null;
    
    // if it's an object, try to extract a single value from it (e.g. for question tables)
    if (!Array.isArray(value) && typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length > 0) {
        value = value[keys[0]];
      } else {
        return null;
      }
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      const nums = value.map(Number).filter(n => !Number.isNaN(n));
      return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) : null;
    }
    
    if (typeof value === 'number') {
      if (Number.isNaN(value)) return null;
      if (questionType === 'TextDate_time' && value > 1440) {
        // ms timestamp stored as number — extract minutes since midnight
        const d = new Date(value);
        return d.getHours() * 60 + d.getMinutes();
      }
      return value;
    }
    if (typeof value === 'string') {
      // time question: handle "HH:MM", "HH:MM:SS", or full ISO datetime
      if (questionType === 'TextDate_time') {
        const timeMatch = value.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
        if (timeMatch) return parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10);
        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
          const d = new Date(value);
          return Number.isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
        }
        return null;
      }
      // date: "YYYY-MM-DD" -> Unix timestamp (ms)
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const ts = new Date(value).getTime();
        return Number.isNaN(ts) ? null : ts;
      }
      // datetime: "YYYY-MM-DDTHH:…" -> Unix timestamp (ms)
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        const ts = new Date(value).getTime();
        return Number.isNaN(ts) ? null : ts;
      }
      const parsed = parseFloat(value);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  }

  private getAnswersArray(sheet: Answersheet): any[] {
    let answersArray = sheet.answers;
    if (typeof answersArray === 'string') {
      try { answersArray = JSON.parse(answersArray); } catch (e) {}
    }
    return Array.isArray(answersArray) ? answersArray : [];
  }

  /**
   * check if a value is numerical
   */
  private isNumerical(value: any, questionType: string): boolean {
    return this.getNumericalValue(value, questionType) !== null;
  }

  private getCategoryId(answer: any): string | null {
    if (answer.position != null) return String(answer.position);
    return null;
  }
}

